/**
 * React Query hooks for the Bot Partners screen. Every mutation invalidates
 * both lists: a restore or a Pilot change shows in the partnerships table,
 * a give-back in the absorptions table.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AbsorptionListParams,
  PartnershipListParams,
  fetchAbsorptions,
  fetchPartnerships,
  restorePartnership,
  reverseAbsorption,
  setPilotPartner
} from '@/api/botPartners';

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['botPartnerships'] });
  qc.invalidateQueries({ queryKey: ['botPartnerAbsorptions'] });
};

export function usePartnerships(params: PartnershipListParams) {
  return useQuery({
    queryKey: ['botPartnerships', params],
    queryFn: () => fetchPartnerships(params),
    refetchInterval: 120_000,
    placeholderData: prev => prev
  });
}

export function useAbsorptions(params: AbsorptionListParams, enabled = true) {
  return useQuery({
    queryKey: ['botPartnerAbsorptions', params],
    queryFn: () => fetchAbsorptions(params),
    enabled,
    refetchInterval: 120_000,
    placeholderData: prev => prev
  });
}

export function useRestorePartnership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; note?: string }) =>
      restorePartnership(vars.id, vars.note),
    onSettled: () => invalidateAll(qc)
  });
}

export function useSetPilotPartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; pilotPartner: boolean; note?: string }) =>
      setPilotPartner(vars.id, vars.pilotPartner, vars.note),
    onSettled: () => invalidateAll(qc)
  });
}

export function useReverseAbsorption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; note?: string }) =>
      reverseAbsorption(vars.id, vars.note),
    onSettled: () => invalidateAll(qc)
  });
}
